import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CTable from '../../components/shared-ui/CTable';
import {
  Avatar,
  Breadcrumb,
  Button,
  Popconfirm,
  Radio,
  Space,
  TableProps,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
} from '@ant-design/icons';
import CTag from '../../components/ui/CTag';
import { useConnection, useUser } from '@sezenta/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import { DataEditModal, useDataEditModal } from '../../common/DataEditModal';
import { FormSchema } from '@sezenta/antd-schema-form';
import UserViewModal, { useUserViewModal } from './UserViewModal';
import AddUserModal from './AddUserModal';
import { useEventListener } from '../../common/EventContext';
import { Permission, User, UserRole } from '../../dto';
import {
  FilterFields,
  FilterPanel,
  FilterValues,
} from '../../common/antd-filter-panel';

const Users = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const connection = useConnection();
  const [users, setUsers] = useState<User[]>([]);
  const editModal = useDataEditModal();
  const viewModal = useUserViewModal();
  const { businessId, select, roles } = useBusinessContext();
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
  const [value, setValue] = useState<FilterValues>([]);

  // server-side pagination + search
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // CR004: email-first attach-or-create flow.
  const [addOpen, setAddOpen] = useState(false);

  const user = useUser();

  const statusFilter = value.find(
    (c) => c.name === 'status' && c.operator === 'eq',
  )?.value as string | undefined;

  const fetchUsers = useCallback(() => {
    setLoading(true);
    connection
      .get(`users/by-businessId/${businessId}`, {
        params: {
          page,
          limit: pageSize,
          search: debouncedSearch || undefined,
          status: statusFilter,
        },
      })
      .then((res) => {
        setUsers(res?.data.data ?? []);
        setTotal(res?.data.total ?? 0);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [connection, businessId, page, pageSize, debouncedSearch, statusFilter]);

  useEffect(() => fetchUsers(), [fetchUsers]);

  // reset to page 1 when the active contract changes
  useEffect(() => setPage(1), [businessId]);

  // debounce search → page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEventListener(
    'USER',
    useCallback(
      (evt) => {
        if (evt.action === 'CREATE') {
          // Refetch rather than append: the create response doesn't carry the
          // resolved per-contract `state` (it lives in user-info), so an appended
          // row would open the edit form with an empty State field -- and saving
          // that would clear the states the user was just given. A refetch also
          // keeps this server-paginated page correctly ordered and sized.
          fetchUsers();
        } else if (evt.action === 'UPDATE') {
          setUsers((prevUser) =>
            prevUser.map((u) => (u.userId === evt.data.userId ? evt.data : u)),
          );
        } else {
          setUsers((prevUser) =>
            prevUser.filter((u) => u.userId !== evt.data.userId),
          );
        }
      },
      [fetchUsers],
    ),
  );

  const roleColors: Record<string, string> = {
    Admin: 'orange',
    Driver: 'green',
    User: 'blue',
  };

  const handleDelete = useCallback(
    async (record: User) => {
      try {
        setLoadingDelete(true);
        await connection.delete(`users/${businessId}/${record.userId}`);
        setUsers((prevState) =>
          prevState.filter((s) => s.userId !== record.userId),
        );
        message.success('User deleted');
        setLoadingDelete(false);
      } catch (e: any) {
        // Surface server-side guard messages (self-delete / last-admin lockout).
        message.error(
          e?.response?.data?.message ?? 'Could not delete this user',
        );
        setLoadingDelete(false);
      }
    },
    [connection, businessId],
  );

  const columns: TableProps<User>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: User) => (
        <Space size="middle">
          {/* <Avatar src={record.profilePic} alt={text} size={35} /> */}
          <Avatar
            src={record.picture ? record.picture : undefined}
            icon={!record.picture ? <UserOutlined /> : undefined}
            alt={text}
          />
          <span
            className="text-teal-500 flex gap-2 items-center cursor-pointer hover:text-blue-500"
            onClick={() => viewModal.current?.view(record)}
          >
            {text}
          </span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Login ID',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Role',
      dataIndex: 'roles',
      key: 'roles',
      render: (roleIds: string[]) => {
        return (
          <>
            {(roleIds ?? []).map((roleId) => {
              const roleName = roles[roleId]?.name;
              return roleName ? (
                <Tag key={roleId} color={roleColors[roleName] || 'default'}>
                  {roleName}
                </Tag>
              ) : null;
            })}
          </>
        );
      },
    },
    {
      title: 'Driver',
      dataIndex: 'isDriver',
      key: 'isDriver',
      render: (val: boolean) => {
        if (val) {
          return <CheckCircleOutlined className={'text-xl text-primary'} />;
        }
        return <></>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (status: string) => {
        const isActive = status === 'Active';
        return (
          <CTag prefix="dot" color={isActive ? 'green' : 'red'}>
            {isActive ? 'ACTIVE' : 'INACTIVE'}
          </CTag>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          {user?.permissions.includes(Permission.EDIT_USER) && (
            <Button
              disabled={[...(record.roles ?? [])].includes(
                UserRole.SUPER_ADMIN,
              )}
              title="Edit User"
              icon={<EditOutlined />}
              type="text"
              className="text-[20px] text-gray-500"
              onClick={() => {
                editModal.current?.edit(
                  record,
                  userEditSchema,
                  'Edit User',
                  `users/${businessId}/${record.userId}`,
                  (r) => ({ ...r, userId: undefined, name: undefined }),
                );
              }}
            ></Button>
          )}
          {/* <Button
            disabled={[...(record.roles ?? [])].includes(UserRole.SUPER_ADMIN)}
            icon={<DeleteOutlined />}
            type="text"
            className="text-[20px] text-gray-500"
          ></Button> */}

          {user?.permissions.includes(Permission.DELETE_USER) &&
            (record.userId === user?.userId ? (
              // You can't delete your own account (lockout / footgun guard).
              <Tooltip
                title="You can't delete your own account"
                placement="bottom"
              >
                <Button
                  icon={<DeleteOutlined />}
                  type="text"
                  disabled
                  className="!text-[20px]"
                />
              </Tooltip>
            ) : (
              <Popconfirm
                okButtonProps={{ loading: loadingDelete }}
                onConfirm={() => handleDelete(record)}
                placement={'topLeft'}
                title={`Are you sure to delete this user?`}
              >
                <Button
                  icon={<DeleteOutlined />}
                  type="text"
                  className="!text-[20px] !text-gray-500"
                />
              </Popconfirm>
            ))}
        </Space>
      ),
    },
  ];

  const userBaseSchema: FormSchema = useMemo(
    () => [
      {
        id: 'userName',
        name: 'User Name',
        type: 'string',
        props: { placeholder: 'User Name' },
        options: {
          rules: [{ required: true, message: 'User Name is required.' }],
        },
      },
      {
        id: 'firstName',
        name: 'First Name',
        type: 'string',
        props: { placeholder: 'First Name' },
        layout: { sm: 12 },
        options: {
          rules: [{ required: true, message: 'First Name is required.' }],
        },
      },
      {
        id: 'lastName',
        name: 'Last Name',
        type: 'string',
        props: { placeholder: 'Last Name' },
        layout: { sm: 12 },
        options: {
          rules: [{ required: true, message: 'Last Name is required.' }],
        },
      },
      {
        id: 'email',
        name: 'Email',
        type: 'email',
        props: { placeholder: 'Email' },
        options: {
          rules: [
            { required: true, message: 'Email is required.' },
            { type: 'email', message: 'Please enter a valid email address.' },
          ],
        },
      },
      {
        id: 'picture',
        name: 'Picture',
        type: 'upload',
        props: {
          placeholder: 'Picture',
          image: { width: 128, height: 128, crop: true },
          acceptSize: 5,
        },
      },
      {
        id: 'roles',
        name: 'Roles',
        type: 'select',
        props: {
          placeholder: 'Select user role',
          options: select.roles,
          mode: 'multiple',
        },
        options: {
          rules: [{ required: true, message: 'User role is required.' }],
        },
      },
      {
        id: 'isDriver',
        name: 'Is Driver',
        type: 'checkbox',
        props: { placeholder: 'Is a Driver' },
        layout: { sm: 12 },
        options: {
          valuePropName: 'checked',
          rules: [{ required: false }],
        },
      },
      {
        id: 'state',
        name: 'State',
        type: 'select',
        props: {
          showSearch: true,
          placeholder: 'Select State',
          options: select?.states ?? [],
          optionFilterProp: 'label',
          mode: 'multiple',
        },
      },
    ],
    [select.roles, select?.states],
  );

  const userCreateSchema = useMemo(
    () => [
      ...userBaseSchema,
      {
        id: 'password',
        name: 'Password',
        type: 'password',
        props: { placeholder: 'Password' },
        options: {
          rules: [{ required: true, message: 'Password is required.' }],
        },
      },
    ],
    [userBaseSchema],
  );

  const userEditSchema = useMemo(
    () => [
      ...userBaseSchema,
      {
        id: 'status',
        name: 'Status',
        type: 'select',
        props: {
          placeholder: 'Select status',
          options: ['Active', 'Inactive'],
        },
        options: {
          rules: [{ required: true, message: 'Status is required.' }],
        },
      },
    ],
    [userBaseSchema],
  );

  const filterSchema: FilterFields = useMemo(
    () => [
      {
        label: 'Status',
        name: 'status',
        operators: ['eq'],
        type: 'radio',
        options: [
          { value: 'Active', label: 'Active' },
          { value: 'Inactive', label: 'Inactive' },
        ],
      },
    ],
    [],
  );

  // Open the full create form, optionally prefilling the email captured in the
  // attach-or-create flow (used for genuinely new users).
  const openCreateForm = useCallback(
    (email?: string) => {
      editModal.current?.create(
        userCreateSchema,
        'Create User',
        'users',
        (data) => {
          data.businessId = businessId;
          return data;
        },
        undefined,
        email ? { email } : undefined,
      );
    },
    [editModal, userCreateSchema, businessId],
  );

  const items = [
    { title: 'Dashboard', href: 'dashboard' },
    { title: 'Admin', href: '#' },
    { title: 'Stores', href: 'stores' },
  ];

  return (
    <div className="w-full h-full">
      <div className="flex flex-col p-5 ctable">
        {/*<div className="w-full pl-3 flex justify-start mb-3">*/}
        {/*  <Breadcrumb separator=">" items={items} />*/}
        {/*</div>*/}
        <div className="flex pl-3 pb-4">
          <div className="flex gap-3 items-center mr-auto">
            <div className="text-[24px] font-[600]">Users</div>
          </div>
        </div>
        <CTable
          loading={loading}
          title="Users"
          columns={columns}
          onSearch={setSearch}
          searchValue={search}
          hideAddButton={!user?.permissions.includes(Permission.CREATE_USER)}
          onRowClick={(record) => viewModal.current?.view(record)}
          data={users}
          pagination={{
            current: page,
            pageSize,
            total,
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (t) => `${t} users`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          addBtnTitle="Add User"
          filterComponent={
            <FilterPanel
              fields={filterSchema}
              value={value}
              onChange={(c) => {
                setValue(c ?? []);
                setPage(1);
              }}
            />
          }
          onAdd={() => setAddOpen(true)}
        />

        <AddUserModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          businessId={businessId}
          roleOptions={select.roles ?? []}
          stateOptions={select.states ?? []}
          onAttached={fetchUsers}
          onCreateNew={openCreateForm}
        />

        <UserViewModal ref={viewModal} />

        <DataEditModal type={'USER'} ref={editModal} />
      </div>
    </div>
  );
};

export default Users;
